import React, { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import PageHeading from '~/components/PageHeading';
import Loading from '~/components/Loading';
import RequiresAuth from '~/components/RequiresAuth.jsx';
import { ThemePreview } from '~/components/forms/StylesThemePicker.jsx';
import {
    invalidateAllFormDetailsQueries,
    invalidateThemesQuery,
    useFormsQuery,
    useThemesQuery,
} from '~/lib/Queries';
import { businessApiRoute, post } from '~/lib/Api';
import * as Toast from '~/lib/Toast.js';
import useBusinessNavigate from '~/lib/useBusinessNavigate';

/**
 * Where the marketing site's theme gallery sends people: /themes/:themeId/apply.
 *
 * That link can't know which business it's for, so this logs in if needed and
 * hands over to /current/, which picks the business the person was last in.
 */
export const ApplyThemeRedirect = () => {
    const { themeId } = useParams();

    return (
        <RequiresAuth>
            <Navigate to={`/current/themes/${themeId}/apply`} replace />
        </RequiresAuth>
    );
};

const FormRow = ({ form, theme, isFirst, onApplied }) => {
    const [isApplying, setIsApplying] = useState(false);
    const isUsingTheme = form.theme_id == theme.id;

    function apply() {
        setIsApplying(true);

        post(businessApiRoute(`/forms/${form.id}/theme`), {
            theme_id: theme.id,
        })
            .then(() => {
                Toast.success(`${theme.name} applied to ${form.name}`);
                onApplied();
            })
            .catch(err => {
                Toast.error(
                    err.response?.data?.message ||
                        'Something went wrong, please try again',
                );
            })
            .finally(() => setIsApplying(false));
    }

    return (
        <div
            className={`flex items-center justify-between py-3 pr-4 ${!isFirst ? 'border-t' : ''}`}
        >
            <div>{form.name}</div>
            <div>
                {isUsingTheme ? (
                    <span className="text-gray-500">Using this theme</span>
                ) : (
                    <button
                        type="button"
                        className="btn-inverse-pill"
                        disabled={isApplying}
                        onClick={apply}
                    >
                        {isApplying ? 'Applying...' : 'Apply'}
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Apply one theme to whichever of this business's forms the person picks.
 *
 * Themes are applied per form, and every public theme is already available to
 * every business, so there is nothing to add to the account first.
 */
const ApplyThemePage = () => {
    const { themeId } = useParams();
    const navigate = useBusinessNavigate();
    const { data: themesData, isLoading: isLoadingThemes } = useThemesQuery();
    const {
        data: forms,
        isLoading: isLoadingForms,
        refetch: refetchForms,
    } = useFormsQuery();

    if (isLoadingThemes || isLoadingForms) {
        return <Loading />;
    }

    const theme = (themesData?.themes || []).find(t => t.id == themeId);

    if (!theme) {
        return (
            <div>
                <PageHeading heading="Apply Theme" />
                <div className="panel p-6 text-gray-500">
                    This theme is no longer available.
                </div>
            </div>
        );
    }

    function afterApply() {
        refetchForms();
        invalidateThemesQuery();
        invalidateAllFormDetailsQueries();
    }

    return (
        <div>
            <PageHeading
                heading={`Apply ${theme.name}`}
                rightContent={
                    <button
                        type="button"
                        className="btn-pill"
                        onClick={() => navigate('/forms')}
                    >
                        Done
                    </button>
                }
            />

            <div className="flex flex-col sm:flex-row gap-6 mb-8 max-w-2xl">
                <div className="w-full sm:w-48 shrink-0 rounded-lg overflow-hidden border">
                    <ThemePreview
                        settings={theme.settings}
                        servedCss={theme.servedCss}
                        name={theme.name}
                    />
                </div>
                <div>
                    <div className="font-bold">{theme.name}</div>
                    {theme.author && (
                        <div className="text-gray-500">by {theme.author}</div>
                    )}
                    {theme.description && (
                        <div className="mt-2">{theme.description}</div>
                    )}
                </div>
            </div>

            <div className="max-w-2xl">
                <div className="panel-label">Choose forms to apply it to</div>
                {forms?.length > 0 ? (
                    <div className="panel pl-4">
                        {forms.map((form, i) => (
                            <FormRow
                                key={form.id}
                                form={form}
                                theme={theme}
                                isFirst={i === 0}
                                onApplied={afterApply}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="panel p-6 text-gray-500">
                        You don't have any forms yet.
                    </div>
                )}
            </div>
        </div>
    );
};

export default ApplyThemePage;
